import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, tokens, Tooltip } from '@hotjar/hotsauce';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import { trackClick } from '@hotjar/webapp/features/Playbacks/utils/tracking';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { useConsoleEventList } from '@hotjar/recordings/src/features/RecordingsPage/components/Console/hooks/useConsoleEventList';
import { EVENT_TYPE } from '@hotjar/recordings/src/features/RecordingsPage/components/Console/types';

const ConsoleToggleWrapper = styled.div<{ isConsoleOpen: boolean }>`
  position: relative;

  ${({ isConsoleOpen }) =>
    isConsoleOpen &&
    `
    Button,
    IconButton,
    Button:hover,
    IconButton:hover {
     background: ${tokens.color.cobalt_200}
    }
  `};
`;

const RedDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 8px;

  position: absolute;
  bottom: 50%;
  left: 12px;
  transform: translate(0px, -2px);

  background-color: ${tokens.color.text_warning};
`;

export const ConsoleButton = ({
  onClick,
  isOpen,
  recording,
  disabled,
}: {
  onClick: () => void;
  isOpen: boolean;
  recording: Recording;
  disabled: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const trackedOnClick = useCallback(() => {
    onClick();

    // We just want to track when console opens
    if (isOpen) return;

    trackClick(recording, {
      element_name: 'Open Console',
      ui_position: 'playback.player_navigation',
      url_event_was_triggered_on: stripDomain(window.location.href),
    });
  }, [recording, onClick, isOpen]);

  const events = useConsoleEventList(recording);

  // TODO: Move to store - RECS-1495
  const errorCount = useMemo(
    () => events.reduce((acc, e) => (e.type === EVENT_TYPE.ERROR ? acc + 1 : acc), 0),
    [events],
  );
  const hasErrors = errorCount > 0;

  const errorCountLabel = errorCount > 99 ? '99+' : `${errorCount}`;

  let buttonLabel = t('common:player.console');
  if (errorCount) {
    buttonLabel =
      errorCount === 1
        ? t('common:player.errors', { count: 1 })
        : `${errorCountLabel} ${t('common:player.multiple-errors')}`;
  }

  return (
    <ConsoleToggleWrapper isConsoleOpen={isOpen} data-testid="recording-console-button">
      <Tooltip text={t(`common:player.console-button-${isOpen ? 'close' : 'open'}`)}>
        <Button
          size="s"
          icon="code_dev"
          aria-label={t(`common:player.console-button-${isOpen ? 'close' : 'open'}`)}
          kind="ghost"
          onClick={trackedOnClick}
          isDisabled={disabled}
        >
          {buttonLabel}
        </Button>
      </Tooltip>

      {hasErrors ? <RedDot /> : null}
    </ConsoleToggleWrapper>
  );
};
